import React, { useState } from "react";
import { Star, Send, X } from "lucide-react";
import { submitReview } from "../../api";
import { toast } from "../../hooks/use-toast";

const EMPTY = { author: "", email: "", location: "", rating: 5, text: "" };

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={`star-pick-${n}`}
          type="button"
          onClick={() => onChange(n)}
          className="p-1"
          aria-label={`${n} stars`}
        >
          <Star
            size={26}
            className={
              n <= value
                ? "fill-neutral-900 text-neutral-900"
                : "text-neutral-300"
            }
          />
        </button>
      ))}
      <span className="ml-2 text-sm text-neutral-600">{value}/5</span>
    </div>
  );
}

export default function ReviewForm({ open, onClose, onSubmitted }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.text.trim()) {
      toast({
        title: "Please complete the required fields",
        description: "Your name and review text are required.",
      });
      return;
    }
    if (form.text.trim().length < 20) {
      toast({
        title: "Review too short",
        description: "Please write at least 20 characters so others get useful feedback.",
      });
      return;
    }
    setSending(true);
    try {
      await submitReview({
        author: form.author.trim(),
        email: form.email.trim() || undefined,
        location: form.location.trim(),
        rating: form.rating,
        text: form.text.trim(),
      });
      setForm(EMPTY);
      toast({
        title: "Thank you for your review!",
        description: "Chef Mete will review it and publish it shortly.",
      });
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err) {
      toast({
        title: "Couldn't submit review",
        description: "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "mt-2 w-full bg-transparent border-b border-neutral-300 py-2 focus:outline-none focus:border-neutral-900 transition-colors text-neutral-900";

  return (
    <div
      className="fixed inset-0 z-[80] bg-neutral-900/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#fbf3e4] border border-neutral-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div>
            <p className="text-xs tracking-[0.3em] text-neutral-500">SHARE YOUR EXPERIENCE</p>
            <h4 className="font-display text-2xl mt-1 text-neutral-900">Write a review</h4>
          </div>
          <button
            onClick={onClose}
            aria-label="close"
            className="w-9 h-9 border border-neutral-300 flex items-center justify-center hover:bg-neutral-900 hover:text-[#fbf3e4] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="text-xs tracking-[0.3em] text-neutral-500">YOUR NAME *</label>
            <input value={form.author} onChange={update("author")} className={inputCls} />
          </div>
          <div>
            <label className="text-xs tracking-[0.3em] text-neutral-500">EMAIL (optional, kept private)</label>
            <input type="email" value={form.email} onChange={update("email")} className={inputCls} />
          </div>
          <div>
            <label className="text-xs tracking-[0.3em] text-neutral-500">LOCATION / EVENT TYPE</label>
            <input
              value={form.location}
              onChange={update("location")}
              placeholder="e.g. Wedding, Manchester"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.3em] text-neutral-500">RATING</label>
            <div className="mt-3">
              <StarPicker value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.3em] text-neutral-500">YOUR REVIEW *</label>
            <textarea
              rows={5}
              value={form.text}
              onChange={update("text")}
              placeholder="Tell others about your experience with Chef Mete..."
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            className="btn-primary w-full bg-neutral-900 text-[#fbf3e4] py-3 font-medium tracking-wide flex items-center justify-center gap-2 hover:bg-neutral-700 disabled:opacity-60"
          >
            {sending ? "Sending..." : (<>Submit Review <Send size={16} /></>)}
          </button>
          <p className="text-[11px] text-neutral-500 text-center">
            Reviews are moderated by Chef Mete before appearing on the site.
          </p>
        </form>
      </div>
    </div>
  );
}
